import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { WaveIcon } from "./Decorations";
import { scrollToId } from "./useLenis";
import { useAuth } from "@/lib/auth";
import { Menu, X } from "lucide-react";

const SECTION_ITEMS = [
    { id: "how", label: "How it works" },
    { id: "features", label: "Features" },
    { id: "pricing", label: "Pricing" },
    { id: "faq", label: "FAQ" },
];

const PAGE_ITEMS = [
    { to: "/voices", label: "Voices" },
    { to: "/pricing", label: "Pricing" },
    { to: "/wordpress", label: "WordPress" },
    { to: "/about", label: "About" },
];

export const Nav = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { user, loading } = useAuth();
    const isLanding = location.pathname === "/";
    const items = isLanding ? SECTION_ITEMS : PAGE_ITEMS;

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll);
        onScroll();
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const handleSection = (it) => {
        setOpen(false);
        if (!isLanding) {
            if (it.route) navigate(it.route);
            else navigate("/", { state: { scrollTo: it.id } });
            return;
        }
        if (it.route && it.route !== "/") { navigate(it.route); return; }
        scrollToId(it.id);
    };

    const ctaLabel = user ? "Dashboard" : "Get Started";
    const ctaTo = user ? "/dashboard" : "/register";

    return (
        <>
            <nav data-testid="floating-nav" className={`fixed left-1/2 -translate-x-1/2 z-50 transition-all duration-500 ${scrolled ? "top-3 sm:top-4" : "top-4 sm:top-6"}`}>
                {/* Desktop pill */}
                <div className="hidden lg:flex items-center gap-1 bg-ink-pill text-cream-100 rounded-full pl-5 pr-2 py-2 shadow-pill border border-white/5 backdrop-blur-md">
                    <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 pr-4 mr-1 border-r border-white/10 hover:opacity-90">
                        <WaveIcon color="#FF5B3E" className="w-5 h-3.5"/>
                        <span className="font-display text-[19px] tracking-tight font-semibold">Audo</span>
                    </Link>
                    <ul className="flex items-center gap-1">
                        {items.map((it) => (
                            <li key={it.id || it.to}>
                                {isLanding ? (
                                    <button data-testid={`nav-${it.id}`} onClick={() => handleSection(it)}
                                        className="px-3.5 py-2 text-[13.5px] font-medium text-cream-100/85 hover:text-cream-100 transition-colors rounded-full hover:bg-white/5">
                                        {it.label}
                                    </button>
                                ) : (
                                    <Link to={it.to} data-testid={`nav-${it.label.toLowerCase()}`} className="px-3.5 py-2 text-[13.5px] font-medium text-cream-100/85 hover:text-cream-100 transition-colors rounded-full hover:bg-white/5">
                                        {it.label}
                                    </Link>
                                )}
                            </li>
                        ))}
                        {!loading && !user && (
                            <li>
                                <Link to="/login" data-testid="nav-login" className="px-3.5 py-2 text-[13.5px] font-medium text-cream-100/85 hover:text-cream-100 transition-colors rounded-full hover:bg-white/5">
                                    Login
                                </Link>
                            </li>
                        )}
                    </ul>
                    <Link to={ctaTo} data-testid="nav-cta" className="ml-2 bg-coral hover:bg-coral-dark text-white text-[13.5px] font-semibold px-4 py-2 rounded-full transition-all hover:shadow-coral inline-flex items-center gap-1">
                        {ctaLabel} →
                    </Link>
                </div>

                {/* Mobile pill */}
                <div className="lg:hidden flex items-center gap-2 bg-ink-pill text-cream-100 rounded-full pl-4 pr-2 py-2 shadow-pill">
                    <Link to="/" data-testid="nav-logo-mobile" className="flex items-center gap-2 pr-3">
                        <WaveIcon color="#FF5B3E" className="w-5 h-3.5"/>
                        <span className="font-display text-[17px] font-semibold">Audo</span>
                    </Link>
                    <button data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)} className="p-2 rounded-full bg-white/5 hover:bg-white/10" aria-label="Toggle menu">
                        {open ? <X size={18}/> : <Menu size={18}/>}
                    </button>
                </div>
            </nav>

            {open && (
                <div data-testid="mobile-menu" className="lg:hidden fixed inset-x-4 top-24 z-40 bg-ink-pill text-cream-100 rounded-3xl p-5 shadow-soft-lg">
                    <ul className="flex flex-col gap-1">
                        {items.map((it) => (
                            <li key={it.id || it.to}>
                                {isLanding ? (
                                    <button onClick={() => handleSection(it)} className="w-full text-left px-4 py-3 rounded-2xl hover:bg-white/5 text-base font-medium">{it.label}</button>
                                ) : (
                                    <Link to={it.to} onClick={() => setOpen(false)} className="block px-4 py-3 rounded-2xl hover:bg-white/5 text-base font-medium">{it.label}</Link>
                                )}
                            </li>
                        ))}
                        {!loading && !user && (
                            <li>
                                <Link to="/login" onClick={() => setOpen(false)} className="block px-4 py-3 rounded-2xl hover:bg-white/5 text-base font-medium">Login</Link>
                            </li>
                        )}
                        <li className="mt-2">
                            <Link to={ctaTo} onClick={() => setOpen(false)} className="block text-center bg-coral text-white px-4 py-3.5 rounded-full font-semibold">
                                {ctaLabel} →
                            </Link>
                        </li>
                    </ul>
                </div>
            )}
        </>
    );
};
